import { ChevronLeftOutlineIcon, ChevronRightOutlineIcon } from '@ikyucom/design-system-icons-react'
import { useCallback } from 'react'
import { useBack } from '~/navigation'
import { useHistoryStack } from '~/navigation/store'
import { cx, sva } from '~/styled-system/css'
import type { RecipeVariantProps } from '~/styled-system/css'
import { hstack } from '~/styled-system/patterns'

const title = sva({
  slots: ['container', 'steps', 'active', 'inactive', 'headerSide'],
  base: {
    container: {
      px: '6',
    },
    steps: hstack.raw({
      justifyContent: 'center',
      gap: '2',
      fontSize: 'md',
      py: '5',
      position: 'sticky',
      top: '0',
      left: '0',
      bg: 'white',
      // RadioButton, Checkbox が z-index 指定されているので...
      zIndex: '20',
    }),
    active: {
      fontWeight: 'bold',
      color: 'gray.800',
    },
    inactive: {
      color: 'gray.500',
    },
  },

  variants: {
    // StackableSlideUpModal type=modal のときの title 埋め込み用
    mode: {
      embed: {
        steps: {
          py: '0',
          borderBottom: 'none',
        },
      },
    },
    // コース選択に戻れるときのヘッダーのスタイル
    isBackToCourseSelectEnabled: {
      true: {
        container: hstack.raw({ justifyContent: 'space-between', alignItems: 'center' }),
        headerSide: {
          flex: '1',
        },
      },
      false: {
        headerSide: {
          display: 'none',
        },
      },
    },
  },
})

type State = 'COURSE_SELECT' | 'NEW_RESERVE_INPUT' | 'RESERVE_SUCCESS'

export function StepTitle({ state, mode }: { state: State } & RecipeVariantProps<typeof title>) {
  const isCourseSelectStepShown = useIsCourseSelectStepShown()
  const [isBackToCourseSelectEnabled, backToCourseSelect] = useBackToCourseSelect()
  const { container, steps, active, inactive, headerSide } = title({
    mode,
    isBackToCourseSelectEnabled,
  })

  return (
    <div className={container}>
      <button
        type="button"
        onClick={backToCourseSelect}
        className={cx(hstack({ gap: '1' }), headerSide)}
      >
        <ChevronLeftOutlineIcon width={13} height={13} className={inactive} />
        戻る
      </button>
      <div className={steps}>
        {isCourseSelectStepShown && (
          <>
            <div className={state === 'COURSE_SELECT' ? active : inactive}>ご予約内容の選択</div>
            <ChevronRightOutlineIcon width={14} height={14} className={inactive} />
          </>
        )}
        <div className={state === 'NEW_RESERVE_INPUT' ? active : inactive}>
          ご予約情報の入力・確認
        </div>
        <ChevronRightOutlineIcon width={14} height={14} className={inactive} />
        <div className={state === 'RESERVE_SUCCESS' ? active : inactive}>予約完了</div>
      </div>
      {/* ヘッダー上の左端に「戻る」、中央にステップを表示するために右端に置く空の要素 */}
      <div className={headerSide}></div>
    </div>
  )
}

// PCではガイドトップと予約モーダル1枚だけを重ね合わせるので、コース選択モーダルを経由したことはscreen stackに残っていない。
// コース選択モーダルを経由したかどうかを調べるために、history stackを使う

function useIsCourseSelectStepShown() {
  const historyStack = useHistoryStack()
  return historyStack.findIndex((s) => s.value === 'COURSE_SELECT') !== -1
}

function useBackToCourseSelect() {
  const historyStack = useHistoryStack()
  const courseIndex = historyStack.findIndex((s) => s.value === 'COURSE_SELECT')
  const newReserveIndex = historyStack.findIndex((s) => s.value === 'NEW_RESERVE_INPUT')
  const isBackToCourseSelectEnabled = courseIndex === -1 ? false : newReserveIndex > courseIndex

  const back = useBack()
  const backToCourseSelect = useCallback(() => {
    back('COURSE_SELECT')
  }, [back])

  return [isBackToCourseSelectEnabled, backToCourseSelect] as const
}
